import { Lightning } from '@lightningjs/sdk'

export const appendSearchParams = (params: object, url: URL) => {
  Object.keys(params).forEach((key) => url.searchParams.append(key, params[key]))

  return url
}

export const stringWithSpace = (str: string) => {
  return str.split('').join(' ')
}

export const stringWithUnderscore = (str: string) => {
  if (str.includes('& ')) {
    return str.split('& ').join('').split(' ').join('_')
  }

  return str.split(' ').join('_')
}

export const hexStrToHexAlpha = (hex: string) => {
  return parseInt(hex?.substring(1), 16) + 0xff000000
}

export const stringToCamelCase = (str: string) => {
  return str
    .replace(/(?:^\w|[A-Z]|\b\w)/g, function (word, index) {
      return index === 0 ? word.toLowerCase() : word.toUpperCase()
    })
    .replace(/\s+/g, '')
}

export const capitalize = (str: string) => {
  return str.charAt(0).toUpperCase() + str.slice(1)
}

export const camelCaseToTitleCaseString = (str: string) => {
  const result = str.replace(/([A-Z])/g, ' $1')
  return result.charAt(0).toUpperCase() + result.slice(1)
}

export const addStr = (str: string, index: number, stringToAdd: string) => {
  return str.substring(0, index) + stringToAdd + str.substring(index, str.length)
}

export type RenderInfo = {
  remainingText: string
  moreTextLines: boolean
  lines: string[]
  precision: number
  wordWidth: number
}

/**
 *
 * @param textTexture - The Lightning Text texture to be used for calculations.
 * @param text - The string to be measured, wrapped or truncated. If none is provided, the texture's string will be used instead.
 * @param precisionOverride - Only used by the cursor position calculation on the native input box. If left empty, the texture's precision will be used instead.
 * @returns a RenderInfo object with some data about the way the text will be rendered on the canvas
 */

export const calculateRenderInfo = (
  textTexture: Lightning.Text,
  text?: string,
  precisionOverride?: number,
): RenderInfo => {
  const renderInfo: RenderInfo = {} as RenderInfo
  const textSettings = getTextTextureArgs(textTexture)
  textSettings.text = text ?? textSettings.text ?? ''
  const context = textTexture.stage.platform.getDrawingCanvas().getContext('2d')
  const precision = precisionOverride ?? textSettings.precision

  const paddingLeft = textSettings.paddingLeft * precision
  const w = textSettings.w * precision
  let wordWrapWidth = textSettings.wordWrapWidth * precision
  const letterSpacing = (textSettings.letterSpacing || 0) * precision
  const textIndent = textSettings.textIndent * precision

  context.font = getFontSetting(
    textSettings.fontFace,
    textSettings.fontStyle,
    textSettings.fontSize,
    precision,
    textTexture.stage.getOption('defaultFontFace'),
  )
  context.textBaseline = textSettings.textBaseline

  let width = w || textTexture.stage.getOption('w')
  let innerWidth = width - paddingLeft
  if (innerWidth < 10) {
    width += 10 - innerWidth
    innerWidth = 10
  }

  if (!wordWrapWidth) {
    wordWrapWidth = innerWidth
  }

  // This the initial split of lines, based on the width of the words
  let linesInfo
  if (textSettings.wordWrap) {
    linesInfo = wrapText(
      context,
      textSettings.maxLines,
      textSettings.text,
      wordWrapWidth,
      letterSpacing,
      textIndent,
    )
  } else {
    linesInfo = { l: textSettings.text.split(/(?:\r\n|\r|\n)/), n: [] }
    let n = linesInfo.l.length
    for (let i = 0; i < n - 1; i++) {
      linesInfo.n.push(i)
    }
  }

  // Here's where the ellipsis ... is added to the last visible line of the wrapped text
  let lines = linesInfo.l
  if (textSettings.maxLines && lines.length > textSettings.maxLines) {
    let usedLines = lines.slice(0, textSettings.maxLines)
    let otherLines = null
    if (textSettings.maxLinesSuffix) {
      let maxLinesSuffixWidth = textSettings.maxLinesSuffix
        ? measureText(context, textSettings.maxLinesSuffix)
        : 0
      let al = wrapText(
        context,
        textSettings.maxLines,
        usedLines[usedLines.length - 1],
        wordWrapWidth - maxLinesSuffixWidth,
        letterSpacing,
        textIndent,
        true,
      )
      usedLines[usedLines.length - 1] = al.l[0] + textSettings.maxLinesSuffix
      otherLines = [al.l.length > 1 ? al.l[1] : '']
    } else {
      otherLines = ['']
    }
    let i,
      n = lines.length
    let j = 0
    let m = linesInfo.n.length
    for (i = textSettings.maxLines; i < n; i++) {
      otherLines[j] += (otherLines[j] ? ' ' : '') + lines[i]
      if (i + 1 < m && linesInfo.n[i + 1]) {
        j++
      }
    }
    renderInfo.remainingText = otherLines.join('\n')
    renderInfo.moreTextLines = true
    lines = usedLines
  } else {
    renderInfo.moreTextLines = false
    renderInfo.remainingText = ''
  }

  renderInfo.lines = lines
  renderInfo.precision = precision
  renderInfo.wordWidth = measureText(context, textSettings.text)

  return renderInfo
}

export const getTextTextureArgs = (texture) => {
  const obj: any = {}
  obj.text = texture._text
  obj.w = texture._w
  obj.fontStyle = texture._fontStyle
  obj.fontSize = texture._fontSize
  obj.fontFace = texture._fontFace
  obj.wordWrap = texture._wordWrap
  obj.wordWrapWidth = texture._wordWrapWidth
  obj.textBaseline = texture._textBaseline
  obj.maxLines = texture._maxLines
  obj.maxLinesSuffix = texture._maxLinesSuffix
  obj.precision = texture._precision
  obj.paddingLeft = texture._paddingLeft
  obj.letterSpacing = texture._letterSpacing
  obj.textIndent = texture._textIndent
  return obj
}

export const getFontSetting = (fontFace, fontStyle, fontSize, precision, defaultFontFace) => {
  let ff = fontFace
  if (!Array.isArray(ff)) {
    ff = [ff]
  }
  let ffs = []
  for (let i = 0, n = ff.length; i < n; i++) {
    let curFf = ff[i]
    if (curFf === null) {
      curFf = defaultFontFace
    }
    if (curFf === 'serif' || curFf === 'sans-serif') {
      ffs.push(curFf)
    } else {
      ffs.push(`"${curFf}"`)
    }
  }
  return `${fontStyle} ${fontSize * precision}px ${ffs.join(',')}`
}

// Returns the width of the given text, based off font style, size, etc. as set in the canvas context
export const measureText = (context, word, space = 0) => {
  if (!space) {
    return context.measureText(word).width
  }
  return word.split('').reduce((acc, char) => {
    if (char === '' || char === '​') {
      return acc
    }
    return acc + context.measureText(char).width + space
  }, 0)
}

export const wrapText = (
  context,
  maxLines,
  text,
  wordWrapWidth,
  letterSpacing,
  indent,
  withTruncation = false,
) => {
  const spaceRegex = / |\u200B/g
  let lines = text.split(/\r?\n/g)
  let allLines = []
  let realNewlines = []

  for (let i = 0; i < lines.length; i++) {
    let resultLines = []
    let result = ''
    let spaceLeft = wordWrapWidth - indent
    let words = lines[i].split(spaceRegex)
    let spaces = lines[i].match(spaceRegex) || []

    for (let j = 0; j < words.length; j++) {
      const space = spaces[j - 1] || ''
      let word = words[j]
      const wordWidth = measureText(context, word, letterSpacing)
      const wordWidthWithSpace = wordWidth + measureText(context, space, letterSpacing)

      if (j === 0 || wordWidthWithSpace > spaceLeft) {
        if (j > 0) {
          if (withTruncation || resultLines.length + 1 === maxLines) {
            const truncatedText = truncateText(context, word, space, letterSpacing, spaceLeft)
            result += truncatedText
          } else if (resultLines.length === 0 && word.includes('-')) {
            // This means the truncation needs to happen on the first line
            const [beforeHyphen, afterHyphen] = word.split('-')
            const beforeWithHyphenAndSpaceWidth = measureText(
              context,
              `${space}${beforeHyphen}-`,
              letterSpacing,
            )

            if (beforeWithHyphenAndSpaceWidth <= spaceLeft) {
              result += `${space}${beforeHyphen}-`
              word = afterHyphen
            }
          }

          resultLines.push(result)
          result = ''
        }
        result += word
        spaceLeft = wordWrapWidth - wordWidth - (j === 0 ? indent : 0)
      } else {
        spaceLeft -= wordWidthWithSpace
        result += space + word
      }
    }

    resultLines.push(result)
    result = ''
    allLines = allLines.concat(resultLines)

    if (i < lines.length - 1) {
      realNewlines.push(allLines.length)
    }
  }

  return { l: allLines, n: realNewlines }
}

export const truncateText = (context, word, space, letterSpacing, spaceLeft) => {
  const letters = space.concat(word).split('')
  let truncatedWord = ''

  for (let k = 0; k < letters.length; k++) {
    const letter = letters[k]
    const wordWidth = measureText(context, truncatedWord + letter, letterSpacing)

    const widthDifference = wordWidth - spaceLeft
    // Some character sequences, like " |" had differences so small that they could fit but due to the
    // remaining space they were not being included as part of the visble string on the second line of the cards
    // This tolerance allows differences that are miniscule to still be considered
    const tolerance = 0.035

    if (wordWidth <= spaceLeft || widthDifference < tolerance) {
      truncatedWord += letter
    } else {
      // Important to break the loop, otherwise characters beyond the current word will get
      // "leaked" into the truncated text
      break
    }
  }

  return truncatedWord
}
